import { analyzeActions } from './actionAnalyzer.js'
import { generateAllReferenceReport } from './allReferenceReport.js'
import { analyzeBlocks } from './blockAnalyzer.js'
import { analyzeControlTypes } from './controlTypeAnalyzer.js'
import { generateCrossReferenceReport } from './crossReferenceReport.js'
import { generateDataJson } from './dataJsonGenerator.js'
import { analyzeFlow } from './flowAnalyzer.js'
import { generateLogicJson } from './logicJsonGenerator.js'
import { analyzeSemantics } from './semanticAnalyzer.js'
import { decodeSstArrayBuffer } from './sstEncoding.js'
import { parseSst } from './sstParser.js'
import { generateUnknownReferenceFrequencyReport } from './unknownReferenceFrequencyReport.js'
import { analyzeVariables } from './variableAnalyzer.js'
import { analyzeVariableState } from './variableStateAnalyzer.js'
import { adaptSstConversionToBoston } from './bostonAdapter.js'
import { hydrateExternalCatalogs } from './catalogFiles.js'

export const SST_CONVERTER_VERSION = Object.freeze({
  integration: 'boston-sst-converter-v1',
  schemaVersion: 1,
  sourceRevision: '20196c440dbecbd07d7dfa8457ad8fa647328f27+working-tree',
})

function flattenFields(fields = []) {
  return fields.flatMap((field) => {
    if (!field) return []
    if (field.type === 'field_row') return flattenFields(field.parts || [])
    if (field.type === 'checkbox-group') return flattenFields(field.options || [])
    return [field]
  })
}

function walkActions(actions = [], visit) {
  actions.forEach((action) => {
    if (!action) return
    visit(action)
    if (action.type === 'if') {
      walkActions(action.then || [], visit)
      walkActions(action.else || [], visit)
    }
  })
}

function programStats(logicJson = {}, dataJson = {}) {
  let actions = 0
  Object.values(logicJson.blocks || {}).forEach((blockActions) => {
    walkActions(blockActions, () => { actions += 1 })
  })

  const runtimeForms = Object.values(logicJson.forms || {})
  const fields = runtimeForms.reduce(
    (total, form) => total + flattenFields(form.fields || []).length,
    0,
  )

  return {
    actions,
    blocks: Object.keys(logicJson.blocks || {}).length,
    forms: runtimeForms.length,
    menus: Object.keys(logicJson.menus || {}).length,
    fields,
    dataJsonChars: JSON.stringify(dataJson || {}).length,
    logicJsonChars: JSON.stringify(logicJson || {}).length,
  }
}

function collectDiagnostics(...groups) {
  const seen = new Set()
  const output = []
  groups.flatMap((group) => (Array.isArray(group) ? group : [])).forEach((diagnostic) => {
    if (!diagnostic) return
    const key = JSON.stringify([
      diagnostic.code || diagnostic.kind || '',
      diagnostic.line || diagnostic.sourceLine || '',
      diagnostic.message || diagnostic.raw || '',
    ])
    if (seen.has(key)) return
    seen.add(key)
    output.push(diagnostic)
  })
  return output
}

/**
 * Convierte SST a un IR canónico y a los artefactos crudos data.json/logic.json.
 * Esta función no llama agentes, no publica y no ejecuta comandos Sabre.
 */
export function convertSst(source, options = {}) {
  const content = String(source == null ? '' : source)
  const fileName = String(options.fileName || options.name || 'script.sst')
  if (!content.trim()) throw new Error('El contenido SST es requerido.')

  const parsed = parseSst(content, fileName)
  let ir = analyzeSemantics(parsed.ast, fileName)
  ir = hydrateExternalCatalogs(ir, options)
  ir = analyzeVariables(parsed.ast, ir)
  ir = analyzeControlTypes(ir)
  ir = analyzeVariableState(parsed.ast, ir)
  const flowAnalysis = analyzeFlow(parsed.ast, ir)
  ir = analyzeActions(parsed.ast, flowAnalysis.ir)
  ir = analyzeBlocks(parsed.ast, ir)

  const dataJson = generateDataJson(ir)
  const logicJson = generateLogicJson(ir)
  const reports = {
    crossReferences: generateCrossReferenceReport(parsed.ast, ir),
    unknownReferenceFrequency: generateUnknownReferenceFrequencyReport(parsed.ast),
    allReferences: generateAllReferenceReport(parsed.ast),
  }
  const diagnostics = collectDiagnostics(
    parsed.diagnostics,
    ir.diagnostics,
    logicJson.diagnostics,
    dataJson?.diagnostics?.warnings,
  )

  return {
    version: SST_CONVERTER_VERSION,
    source: {
      fileName,
      encoding: options.encoding || 'text',
      lineCount: content.split(/\r?\n/).length,
    },
    ast: options.includeAst === true ? parsed.ast : undefined,
    ir,
    flowGraph: flowAnalysis.flowGraph,
    dataJson,
    logicJson,
    reports,
    diagnostics,
    stats: programStats(logicJson, dataJson),
  }
}

export function convertSstArrayBuffer(buffer, options = {}) {
  const decoded = decodeSstArrayBuffer(buffer)
  return convertSst(decoded.content, {
    ...options,
    encoding: decoded.encoding,
  })
}

export function convertSstToBoston(source, options = {}) {
  const conversion = convertSst(source, options)
  return {
    ...conversion,
    boston: adaptSstConversionToBoston(conversion, options),
  }
}

export function convertSstArrayBufferToBoston(buffer, options = {}) {
  const decoded = decodeSstArrayBuffer(buffer)
  return convertSstToBoston(decoded.content, {
    ...options,
    encoding: decoded.encoding,
  })
}

export { adaptSstConversionToBoston, decodeSstArrayBuffer }
